import type { LucideIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../lib/utils.js";

/**
 * ProductTile (W-2 Home "Protect something new"): a full-tile link with an
 * icon and a label. Pass the link element (router Link or `<a>`) as the
 * only child; the tile renders its content inside it. Label translated.
 */

export interface ProductTileProps {
  label: string;
  icon?: LucideIcon;
  /** Optional second line, e.g. who covers it. */
  hint?: string;
  className?: string;
  children: React.ReactElement<{ className?: string; children?: React.ReactNode }>;
}

function ProductTile({ label, icon: Icon, hint, className, children }: ProductTileProps) {
  const link = children as React.ReactElement<Record<string, unknown> & { className?: string }>;
  return React.cloneElement(
    link,
    {
      className: cn(
        "flex min-h-24 flex-col items-start justify-between gap-2 rounded-lg border bg-card p-4 text-card-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring",
        link.props.className,
        className,
      ),
      "data-slot": "product-tile",
    },
    <>
      {Icon ? <Icon aria-hidden="true" className="size-6 text-primary" /> : null}
      <span className="flex flex-col">
        <span className="font-semibold">{label}</span>
        {hint ? <span className="text-sm text-muted-foreground">{hint}</span> : null}
      </span>
    </>,
  );
}

export { ProductTile };
